import type { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { reactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { tryGetShortcutKey } from 'components/WorkspaceEditor/utils/shortcutKeyUtils'
import { type KeyboardEvent, useCallback, useRef } from 'react'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { insertingUOMViaKeyboardPills, pillInlineTextBeforeUOM } from '../uomPillState'

export const useUOMInlinePillHandlers = (
    contentItem: Partial<TextContentElement>,
    inlineText: string
) => {
    const inlineTextRef = useRef(inlineText)
    inlineTextRef.current = inlineText

    // Returns true if UOM consumed the blur event — caller should return early.
    const handleInlineBlurUOM = useCallback((): boolean => {
        // Action bar button mousedown fires before blur — capture typed text so
        // buildUOMHandlerForNewPill can use it, then defer to the button's click handler.
        if (useWorkspace2Store.getState().isActionButtonMousedown) {
            if (contentItem.id && inlineTextRef.current) {
                pillInlineTextBeforeUOM.set(contentItem.id, inlineTextRef.current)
            }
            return true
        }
        // Ctrl+U was pressed — the UOM scaffold is being mounted; skip normal blur logic.
        if (contentItem.id && insertingUOMViaKeyboardPills.has(contentItem.id)) {
            const pillId = contentItem.id
            // Disarm via microtask (after this burst) so it can't stay armed forever.
            queueMicrotask(() => insertingUOMViaKeyboardPills.delete(pillId))
            return true
        }
        return false
    }, [contentItem.id])

    // Returns true if Ctrl+U was handled — caller should return early; shouldCaptureText=true preserves inline typed value.
    const handleInsertUOMKeyDown = useCallback(
        (e: KeyboardEvent, shouldCaptureText = false): boolean => {
            if (!tryGetShortcutKey('InsertUOM', e)) return false
            // Claim the shortcut immediately — prevents browser Ctrl+U (View Source) in all guard paths.
            e.preventDefault()
            // Ignore auto-repeat so one keypress inserts exactly one UOM.
            if (e.repeat) return true
            const handler = reactiveWorkspaceActions().omWorkspaceInsertUOMHandler
            // No handler means UOM is already active for this pill — consumed but nothing to do.
            if (!handler) return true
            // Prevents a duplicate fire when Ctrl+U bubbles from an inner handler to an outer one.
            if (contentItem.id && insertingUOMViaKeyboardPills.has(contentItem.id)) return true
            if (contentItem.id) {
                const pillId = contentItem.id
                insertingUOMViaKeyboardPills.add(pillId)
                // Released event-driven (not on a timer) once React commits the insert —
                // see the release sites in useUOMPillBehavior and handleInlineBlurUOM above.
                if (shouldCaptureText && inlineTextRef.current) {
                    pillInlineTextBeforeUOM.set(pillId, inlineTextRef.current)
                }
            }
            handler()
            return true
        },
        [contentItem.id]
    )

    return { handleInlineBlurUOM, handleInsertUOMKeyDown }
}
